import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Checkbox, Input, Space, Table, Tag } from "antd";
import {
  ArrowUpOutlined,
  BulbFilled,
  BulbOutlined,
  FullscreenExitOutlined,
  FilterOutlined,
  FullscreenOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useSemaforoAsignacion } from "../../../hooks/useAsistenciasSemaforo";
import { zonasRojas } from "../../detalleServicio/zonaRoja/zonasRojas";
import "./SemaforoAsignacionMonitor.css";

const crearOpciones = (datos, campo) =>
  [...new Set(datos.map((item) => item[campo]).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, "es"))
    .map((valor) => ({ text: valor, value: valor }));

const normalizarTexto = (texto) =>
  String(texto ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

const esOrigenZonaRoja = (origen) => {
  const [municipio, estado] = String(origen || "").split("/");

  return normalizarTexto(estado) === "veracruz"
    && zonasRojas.some((zona) => normalizarTexto(municipio).includes(normalizarTexto(zona)));
};

const filtroTexto = (campo, etiqueta) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div
      className="asignacion-monitor__filtro-texto"
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Input
        autoFocus
        placeholder={`Buscar ${etiqueta.toLowerCase()}`}
        value={selectedKeys[0] || ""}
        onChange={(event) => {
          setSelectedKeys(event.target.value ? [event.target.value] : []);
          confirm({ closeDropdown: false });
        }}
        onPressEnter={() => confirm()}
      />
      <Space>
        <Button
          onClick={() => {
            clearFilters?.();
            confirm({ closeDropdown: false });
          }}
          size="small"
        >
          Limpiar búsqueda
        </Button>
      </Space>
    </div>
  ),
  filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#596a7c" : undefined }} />,
  onFilter: (valor, registro) => normalizarTexto(registro[campo]).includes(normalizarTexto(valor)),
});

const FiltroMultipleDropdown = ({ datos, campo, setSelectedKeys, selectedKeys = [], confirm }) => {
  const [busqueda, setBusqueda] = useState("");
  const opciones = crearOpciones(datos, campo).filter((opcion) =>
    normalizarTexto(opcion.text).includes(normalizarTexto(busqueda))
  );

  const cambiarSeleccion = (valor, seleccionado) => {
    const siguientes = seleccionado
      ? [...selectedKeys, valor]
      : selectedKeys.filter((actual) => actual !== valor);
    setSelectedKeys(siguientes);
    confirm({ closeDropdown: false });
  };

  return (
    <div
      className="asignacion-monitor__filtro-multiple"
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Input
        allowClear
        autoFocus
        onChange={(event) => setBusqueda(event.target.value)}
        placeholder="Buscar opción"
        value={busqueda}
      />
      <div className="asignacion-monitor__filtro-opciones">
        {opciones.map((opcion) => (
          <Checkbox
            checked={selectedKeys.includes(opcion.value)}
            key={opcion.value}
            onChange={(event) => cambiarSeleccion(opcion.value, event.target.checked)}
          >
            {opcion.text}
          </Checkbox>
        ))}
        {opciones.length === 0 && <span className="asignacion-monitor__sin-opciones">Sin coincidencias</span>}
      </div>
      {selectedKeys.length > 0 && (
        <div className="asignacion-monitor__filtro-pie">
          <span>{selectedKeys.length} seleccionada(s)</span>
          <Button
            onClick={() => {
              setSelectedKeys([]);
              confirm({ closeDropdown: false });
            }}
            size="small"
            type="link"
          >
            Limpiar
          </Button>
        </div>
      )}
    </div>
  );
};

const filtroMultiple = (datos, campo) => ({
  filterDropdown: (props) => <FiltroMultipleDropdown {...props} campo={campo} datos={datos} />,
  filterIcon: (filtered) => <FilterOutlined style={{ color: filtered ? "#596a7c" : undefined }} />,
  onFilter: (valor, registro) => registro[campo] === valor,
});

const opcionesEstatus = [
  { text: "Escalamiento", value: "escalamiento" },
  { text: "En espera de usuario", value: "esperaUsuario" },
  { text: "Zona roja", value: "zonaRoja" },
  { text: "Sin distintivo", value: "sinDistintivo" },
];

const DistintivoEstatus = ({ tipo }) => {
  if (tipo === "escalamiento") {
    return <span title="Escalamiento"><ArrowUpOutlined className="asignacion-monitor__estatus-escalamiento" /></span>;
  }
  if (tipo === "esperaUsuario") {
    return (
      <span title="Servicio en espera de usuario">
        <svg className="asignacion-monitor__estatus-espera" viewBox="0 0 32 32" aria-hidden="true">
          <path d="M11 14V6a2 2 0 0 1 4 0v7-9a2 2 0 0 1 4 0v9-7a2 2 0 0 1 4 0v8-5a2 2 0 0 1 4 0v10a8 8 0 0 1-8 8h-3a7 7 0 0 1-5.6-2.8l-4.8-6a2.5 2.5 0 0 1 3.8-3.2L11 18v-4Z" />
        </svg>
      </span>
    );
  }
  if (tipo === "zonaRoja") {
    return (
      <span title="Zona roja">
        <span className="asignacion-monitor__estatus-zona-roja" />
      </span>
    );
  }
  return <span className="asignacion-monitor__estatus-sin-distintivo" title="Sin distintivo">—</span>;
};

const FiltroEstatusDropdown = ({ setSelectedKeys, selectedKeys = [], confirm }) => (
  <div className="asignacion-monitor__filtro-multiple asignacion-monitor__filtro-estatus" onKeyDown={(event) => event.stopPropagation()}>
    <div className="asignacion-monitor__filtro-opciones">
      {opcionesEstatus.map((opcion) => (
        <Checkbox
          checked={selectedKeys.includes(opcion.value)}
          key={opcion.value}
          aria-label={opcion.text}
          title={opcion.text}
          onChange={(event) => {
            const siguientes = event.target.checked
              ? [...selectedKeys, opcion.value]
              : selectedKeys.filter((valor) => valor !== opcion.value);
            setSelectedKeys(siguientes);
            confirm({ closeDropdown: false });
          }}
        >
          <span className="asignacion-monitor__filtro-estatus-label">
            <span className="asignacion-monitor__filtro-estatus-icono">
              <DistintivoEstatus tipo={opcion.value} />
            </span>
            <span>{opcion.text}</span>
          </span>
        </Checkbox>
      ))}
    </div>
    {selectedKeys.length > 0 && (
      <div className="asignacion-monitor__filtro-pie">
        <span>{selectedKeys.length} seleccionada(s)</span>
        <Button
          onClick={() => {
            setSelectedKeys([]);
            confirm({ closeDropdown: false });
          }}
          size="small"
          type="link"
        >
          Limpiar
        </Button>
      </div>
    )}
  </div>
);

const crearColumnas = (datos) => [
  {
    title: "",
    dataIndex: "estatus",
    width: 75,
    align: "center",
    filterDropdown: (props) => <FiltroEstatusDropdown {...props} />,
    filterIcon: (filtered) => <FilterOutlined style={{ color: filtered ? "#596a7c" : undefined }} />,
    onFilter: (valor, registro) => {
      if (valor === "escalamiento") return registro.fechaEscalamientoAsignacion != null;
      if (valor === "esperaUsuario") return Boolean(registro.enEsperaUsuario);
      if (valor === "zonaRoja") return esOrigenZonaRoja(registro.origen);
      if (valor === "sinDistintivo") {
        return registro.fechaEscalamientoAsignacion == null
          && !registro.enEsperaUsuario
          && !esOrigenZonaRoja(registro.origen);
      }
      return false;
    },
    render: (_, registro) => (
      <span className="asignacion-monitor__estatus">
        {registro.fechaEscalamientoAsignacion != null && (
          <DistintivoEstatus tipo="escalamiento" />
        )}
        {registro.enEsperaUsuario && (
          <DistintivoEstatus tipo="esperaUsuario" />
        )}
        {esOrigenZonaRoja(registro.origen) && (
          <DistintivoEstatus tipo="zonaRoja" />
        )}
      </span>
    ),
  },
  // { title: "#", dataIndex: "numero", width: 42, align: "center", fixed: "left" },
  { title: "ID", dataIndex: "id", width: 150, sorter: (a, b) => a.id.localeCompare(b.id), ...filtroTexto("id", "ID") },
  { title: "Origen", dataIndex: "origen", width: 230, ...filtroTexto("origen", "origen") },
  { title: "Servicio", dataIndex: "servicio", width: 145, ...filtroMultiple(datos, "servicio") },
  { title: "Creación", dataIndex: "creacion", width: 135, sorter: (a, b) => (a.creacion || 0) - (b.creacion || 0), render: (fecha) => fecha ? new Date(fecha).toLocaleString("es-MX", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).replace(",", "") : "—" },
  { title: "Operador", dataIndex: "operador", width: 170, ...filtroTexto("operador", "operador") },
  // { title: "STATUS", dataIndex: "status", width: 135, render: () => null },
  {
    title: "Tipo de Atención",
    dataIndex: "tipoAtencion",
    width: 165,
    align: "center",
    ...filtroMultiple(datos, "tipoAtencion"),
    render: (tipo) => (
      <Tag className={`asignacion-monitor__tipo${tipo === "VIP" ? " asignacion-monitor__tipo--vip" : ""}`}>
        {tipo}
      </Tag>
    ),
  },
  {
    title: "Tiempo Encolada",
    dataIndex: "tiempoEncolado",
    width: 175,
    align: "center",
    fixed: "right",
    sorter: (a, b) => (a.tiempoEncolado ?? -1) - (b.tiempoEncolado ?? -1),
    defaultSortOrder: "descend",
    render: (segundos) => {
      if (segundos === null) return "—";
      const tiempo = [Math.floor(segundos / 3600), Math.floor(segundos % 3600 / 60), segundos % 60]
        .map((valor) => String(valor).padStart(2, "0")).join(":");
      const nivel = segundos >= 1800 ? "alto" : segundos >= 900 ? "medio" : "bajo";
      return <span className={`asignacion-monitor__tiempo asignacion-monitor__tiempo--${nivel}`}>{tiempo}</span>;
    },
  },
].map((columna) => ({
  ...columna,
  ellipsis: { showTitle: false },
  onCell: (registro) => ({
    title: columna.dataIndex === "creacion"
      ? (registro.creacion ? new Date(registro.creacion).toLocaleString("es-MX") : "—")
      : columna.dataIndex === "tiempoEncolado" ? undefined : String(registro[columna.dataIndex] ?? ""),
  }),
}));

export const SemaforoAsignacionMonitor = () => {
  const history = useHistory();
  const { asistencias, loading, error } = useSemaforoAsignacion();
  const [ahora, setAhora] = useState(Date.now());
  const [vistaFocus, setVistaFocus] = useState(false);
  const [modoOscuro, setModoOscuro] = useState(false);

  useEffect(() => {
    const intervalo = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(intervalo);
  }, []);

  useEffect(() => {
    if (!vistaFocus) return undefined;

    const salirConEscape = (event) => {
      if (event.key === "Escape") setVistaFocus(false);
    };

    window.addEventListener("keydown", salirConEscape);
    return () => window.removeEventListener("keydown", salirConEscape);
  }, [vistaFocus]);

  const datos = asistencias.map((asistencia, index) => ({
    key: asistencia.asistenciaID,
    numero: index + 1,
    id: asistencia.asistenciaID,
    origen: asistencia.direccionOrigen || "",
    fechaEscalamientoAsignacion: asistencia.fechaEscalamientoAsignacion,
    enEsperaUsuario: asistencia.enEsperaUsuario,
    servicio: asistencia.servicio || "",
    creacion: asistencia.fechaLlamadaIniciada,
    operador: (asistencia.nombreOperador || "").trim(),
    tipoAtencion: [asistencia.cuentaEspecialGS, asistencia.cuentaEspecialGNP]
      .some((valor) => valor !== null && valor !== undefined && String(valor).trim() !== "")
      ? "VIP" : "Normal",
    tiempoEncolado: asistencia.fechaCapturaTerminada === null ? null
      : Math.max(0, Math.floor((ahora - asistencia.fechaCapturaTerminada) / 1000)),
  }));

  return (
    <div className={`asignacion-monitor-layout${vistaFocus ? " asignacion-focus" : ""}${modoOscuro ? " asignacion-dark" : ""}`}>
      <header className="asignacion-monitor__header">
        <Card size="small" className="asignacion-monitor__modulo-titulo">
          <span className="asignacion-monitor__modulo-etiqueta">SEMÁFORO / MONITOR</span>
          <h1 className="asignacion-monitor__titulo">Asignación</h1>
        </Card>
        {!vistaFocus && (
          <Card size="small" className="asignacion-monitor__acciones">
            <span className="asignacion-monitor__modulo-etiqueta">CONTROLES DE VISTA</span>
            <div className="asignacion-monitor__acciones-botones">
            <Button
              aria-label="Vista Focus"
              icon={<FullscreenOutlined />}
              onClick={() => setVistaFocus(true)}
              title="Vista Focus"
              type="text"
            />
            <Button
              aria-label={modoOscuro ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
              icon={modoOscuro ? <BulbFilled /> : <BulbOutlined />}
              onClick={() => setModoOscuro((actual) => !actual)}
              title={modoOscuro ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
              type="text"
            />
            </div>
          </Card>
        )}
      </header>
      <main className="asignacion-monitor__contenido">
        {error && <Alert type="error" showIcon message="No se pudo consultar el semáforo de asignación" />}
        <Table
          className="asignacion-monitor"
          columns={crearColumnas(datos)}
          dataSource={datos}
          loading={loading}
          rowKey="key"
          onRow={(registro) => ({
            onClick: () => history.push(`/detalleAsistencia/${registro.id}`),
          })}
          size="small"
          pagination={false}
          scroll={{ x: 1490 }}
          locale={{ emptyText: "No hay asignaciones que coincidan con los filtros" }}
        />
      </main>
        {vistaFocus && (
          <Card size="small" className="asignacion-focus__footer">
            <span className="asignacion-monitor__modulo-etiqueta">FOCUS <span aria-hidden="true">/</span> <kbd>Esc</kbd> para salir</span>
            <div className="asignacion-monitor__footer-controles">
            <span className="asignacion-monitor__modulo-etiqueta">CONTROLES DE VISTA</span>
            <div className="asignacion-monitor__acciones-botones">
            <Button
              aria-label="Salir de Focus"
              icon={<FullscreenExitOutlined />}
              onClick={() => setVistaFocus(false)}
              title="Salir de Focus (Esc)"
              type="text"
            />
            <Button
              aria-label={modoOscuro ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
              icon={modoOscuro ? <BulbFilled /> : <BulbOutlined />}
              onClick={() => setModoOscuro((actual) => !actual)}
              title={modoOscuro ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
              type="text"
            />
            </div>
            </div>
          </Card>
        )}
    </div>
  );
};
